import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import { wa, WA_MESSAGES, scrollToId, IMG } from "@/lib/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-ivory pt-[72px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full bg-gold-light/40 blur-3xl"
        style={{ position: "absolute" }}
      />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-16 md:px-10 lg:grid-cols-12 lg:gap-6">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="mb-8 flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-12 bg-gold" />
            MAHIRA Agro Products
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-heading text-[13.5vw] font-medium leading-[1.02] text-forest sm:text-6xl lg:text-[5.4rem]"
          >
            <MaskedLine delay={0.25}>Pure by Tradition.</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-forest-light">
              Made for the World.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-charcoal/70 md:text-lg"
          >
            Premium A2 Gir Cow Ghee, inspired by India&apos;s rich dairy
            tradition and created for families who value authentic food.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-shop-cta"
              onClick={() => scrollToId("product")}
              className="group inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-forest-light"
            >
              Shop MAHIRA Ghee
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              data-testid="hero-whatsapp-cta"
              href={wa(WA_MESSAGES.default)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-forest/25 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-forest transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold hover:bg-gold/10"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Us
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imgY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
            className="relative mx-auto w-[78%] max-w-[380px] lg:w-full"
          >
            <div
              aria-hidden
              className="absolute -inset-3 rounded-t-[999px] rounded-b-[28px] border border-gold/50"
            />
            <div className="animate-float-slow overflow-hidden rounded-t-[999px] rounded-b-[24px] shadow-[0_40px_90px_-30px_rgba(26,54,38,0.45)]">
              <img
                data-testid="hero-product-image"
                src={IMG.heroJar}
                alt="MAHIRA A2 Gir Cow Ghee in a premium glass jar with a brass spoon"
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="absolute -left-8 bottom-10 hidden rounded-2xl border border-forest/10 bg-ivory/90 px-5 py-4 shadow-[0_20px_50px_-20px_rgba(26,54,38,0.35)] backdrop-blur-md sm:block"
              data-testid="hero-product-badge"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                Flagship
              </p>
              <p className="mt-1 font-heading text-xl text-forest">
                A2 Gir Cow Ghee
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("why-mahira")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-forest/50 transition-colors duration-300 hover:text-forest md:flex"
        aria-label="Scroll to explore"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.4em]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
}
